import { useEffect, useRef } from 'react';
import { useLaunchDarkly } from './hooks';
import { createLDUser } from './context';

/**
 * Custom hook to identify a user to LaunchDarkly when their information becomes available
 * This hook will update the LaunchDarkly context with the user's email when it becomes available
 *
 * @param {Object} user - The user object from Redux
 * @returns {Object} - An object containing the identify function and refs for testing/debugging
 */
export const useIdentifyUser = user => {
  const { identify } = useLaunchDarkly();
  const emailUpdatedRef = useRef(false);
  const lastEmailRef = useRef('');

  // Update LaunchDarkly context when user information becomes available
  useEffect(() => {
    // Skip if user is not available
    if (!user) return;

    // Case 1: User has an email and either:
    // 1. We haven't updated the email yet, or
    // 2. The email has changed since our last update
    if (
      user.email &&
      (!emailUpdatedRef.current || lastEmailRef.current !== user.email)
    ) {
      // Create LaunchDarkly user object with email
      const ldUser = createLDUser({
        userId: user.userId,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });

      // Identify the user to LaunchDarkly
      identify(ldUser)
        .then(() => {
          // Mark that we've updated the email to prevent multiple calls
          emailUpdatedRef.current = true;
          // Store the email we just updated with
          lastEmailRef.current = user.email;
        })
        .catch(error => {
          // Log error but don't throw
          console.error('Error identifying user to LaunchDarkly:', error);
        });
    }
    // Case 2: User has a userId but no email yet (early identification)
    else if (user.userId && !user.email && !emailUpdatedRef.current) {
      // Create LaunchDarkly user object with just userId
      const ldUser = createLDUser({
        userId: user.userId,
      });

      // Identify the user to LaunchDarkly
      identify(ldUser)
        .then(() => {
          console.log('LaunchDarkly user identified with userId:', user.userId);
          // Don't mark emailUpdatedRef as true yet, so we'll update again when email is available
        })
        .catch(error => {
          // Log error but don't throw
          console.error('Error identifying user to LaunchDarkly:', error);
        });
    }
  }, [user, identify]);

  /**
   * Reset the user identification state
   * Call this when logging out
   */
  const resetIdentification = () => {
    emailUpdatedRef.current = false;
    lastEmailRef.current = '';

    // Get the current domain
    const domain =
      typeof window !== 'undefined' ? window.location.hostname : '';

    // Identify as anonymous user in LaunchDarkly with domain information
    return identify({
      key: 'anonymous-user',
      anonymous: true,
      custom: {
        domain: domain,
      },
    }).catch(() => {
      // Silently handle errors
    });
  };

  return {
    identify,
    resetIdentification,
    // Expose refs for testing/debugging
    _refs: {
      emailUpdatedRef,
      lastEmailRef,
    },
  };
};

export default useIdentifyUser;
